'use strict';

/**
 * Page-load gate that holds the AJAX burst until supplier resolution has committed.
 *
 * Geo-IP resolution commits the fulfillment hash to the session. The session is a single blob that
 * every request rewrites wholesale, so a request already in flight when that commit lands writes
 * back the blob it read beforehand and erases the hash. Resolving ahead of the burst leaves nothing
 * in flight to overwrite it.
 *
 * Mirrors the CSRF bootstrap gate (`__rp_csrf_ready_promise`), including exposing the promise on
 * `globalThis` so `installAjaxDeferralWrapper` can read it without importing this module.
 */

const SUPPLIER_READY_PROMISE_KEY = '__rp_supplier_ready_promise';

/**
 * The request that settles the gate, so it must never be held by the gate. Shared with the CSRF
 * deferral exemption list: if the two drift apart the page deadlocks until the bootstrap timeout.
 */
const SUPPLIER_RESOLUTION_ENDPOINT = '/ajax/ip-location/get';

/**
 * Matches the CSRF bootstrap's own backstop. Duplicated rather than imported because
 * `csrf/ajax-hooks.js` requires `csrf/utils.js`, which requires this module — reaching back into the
 * CSRF tree from here would close that loop.
 */
const SUPPLIER_READY_TIMEOUT_MS = 20000;

let supplierReadyPromise = null;
let resolveSupplierReady = null;

/**
 * Opens the gate. Entry points that will resolve a supplier must call this before the CSRF
 * bootstrap installs the deferral wrapper, otherwise the wrapper captures no supplier gate and the
 * burst races the commit. Whoever opens it owns settling it via `markSupplierReady()`; until then
 * every deferred request waits, bounded only by the bootstrap timeout.
 *
 * @returns {Promise<void>}
 */
function openSupplierReadyGate() {
	if (supplierReadyPromise === null) {
		supplierReadyPromise = new Promise((resolve) => {
			resolveSupplierReady = resolve;
		});
		globalThis[SUPPLIER_READY_PROMISE_KEY] = supplierReadyPromise;
	}

	return supplierReadyPromise;
}

/**
 * Releases the burst. Safe to call when the gate was never opened, and safe to call twice.
 *
 * @returns {void}
 */
function markSupplierReady() {
	if (resolveSupplierReady === null) {
		return;
	}

	resolveSupplierReady();
	resolveSupplierReady = null;
}

/**
 * Resolves once supplier resolution has settled. Resolves immediately when no gate was opened
 * (iframed pages, plugin contexts), so callers are never left waiting on a page that will never
 * resolve a supplier.
 *
 * Bounded because the gate opens at module scope but settles in `$onReady`, so anything that breaks
 * the page between those two points would otherwise block callers forever. The `$ajax` wrapper has
 * its own equivalent backstop; this covers the `fetch` callers, which have none.
 *
 * @returns {Promise<void>}
 */
function waitForSupplierResolution() {
	const gate = globalThis[SUPPLIER_READY_PROMISE_KEY];
	if (!gate || typeof gate.then !== 'function') {
		return Promise.resolve();
	}

	let releaseTimeoutId = null;
	const releaseOnTimeout = new Promise((resolve) => {
		releaseTimeoutId = setTimeout(() => {
			// Settles the gate itself rather than just this caller, so the backstop fires once for
			// the whole page the way the `$ajax` wrapper's one-time `settled` flag does. Releasing
			// only the current caller would start a fresh 20s wait for every later one.
			markSupplierReady();
			resolve();
		}, SUPPLIER_READY_TIMEOUT_MS);
	});

	return Promise.race([gate, releaseOnTimeout]).then(() => {
		clearTimeout(releaseTimeoutId);
	});
}

module.exports = {
	SUPPLIER_READY_PROMISE_KEY,
	SUPPLIER_RESOLUTION_ENDPOINT,
	markSupplierReady,
	openSupplierReadyGate,
	waitForSupplierResolution,
};
